// src/pages/Scan.tsx
import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import media from '../style/media';
import TopBar from '../components/TopBar';
import SettingsBars from '../components/SettingsBars';
import { useCameras } from '../hooks';
import { useGetPatientByIdQuery, useInsertLicenseUsageMutation } from '../services/patientApi';
import { StorageKeys } from '../utils/storage';
import { Flex } from '../components/shared/Flex';


import { PageContainer, ContentWrapper, Header, Title, Subtitle, GlassCard } from '../style/layout';
import { Zap, Activity, ChevronRight } from 'lucide-react';


const SelectionCard = styled.div<{ variant?: 'enhanced' | 'basic' }>`
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 24px;
  padding: 2rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);

  ${props => props.variant === 'enhanced' && `
    border-color: rgba(59, 130, 246, 0.1);
    background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #3b82f6, #10b981);
    }
  `}

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
    border-color: #e2e8f0;

    .icon-wrapper {
      transform: scale(1.1);
      background: #f1f5f9;
    }
    
    .arrow-icon {
      transform: translateX(4px);
      color: #3b82f6;
    }
  }

  &:active {
    transform: translateY(-4px);
  }
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  color: #3b82f6;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const Badge = styled.div`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: #10b981;
  color: white;
  font-size: 0.7rem;
  font-weight: 800;
  padding: 0.4rem 0.8rem;
  border-radius: 50px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
`;

const CardDescription = styled.p`
  font-size: 0.9375rem;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #94a3b8;
  font-size: 0.875rem;
  font-weight: 600;
  margin-top: auto;
  transition: all 0.3s ease;
`;

const SelectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  ${media.tablet`
    grid-template-columns: 1fr;
  `}
`;

const Scan = () => {
  const navigate = useNavigate();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [insertLicenseUsage] = useInsertLicenseUsageMutation();
  const cameras = useCameras();

  const pid = useMemo(() => localStorage.getItem(StorageKeys.PATIENT_ID) || '', []);
  console.log('pid', pid);
  const { data: patientData } = useGetPatientByIdQuery(pid, { skip: !pid });
  console.log('patientData', patientData);

  const toggleSettings = useCallback(() => {
    setIsSettingsOpen(prev => !prev);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  const updateLicenseStatus = useCallback((valid: boolean) => {
    setIsLicenseValid(valid);
  }, []);

  const handleEnhancedMeasurement = () => {
    navigate('/enhancedScan');
  };

  const handleBasicMeasurement = async () => {
    try {
      const patientId = localStorage.getItem(StorageKeys.PATIENT_ID);
      const licenseId = patientData?.patient?.license_id;
      const accessCode = patientData?.patient?.access_code;

      if (patientId && licenseId && accessCode) {
        console.log('License usage API called');
        const response = await insertLicenseUsage({
          license_id: licenseId,
          patient_id: patientId,
          access_code: accessCode,
        }).unwrap();
        console.log("License usage response:", response);

        // Save license usage ID
        if (response?.licenseUsage?.id) {
          const usageId = response.licenseUsage.id;

          console.log("Saving LICENSE_USAGE_ID →", usageId);

          localStorage.setItem(StorageKeys.LICENSE_USAGE_ID, usageId);

          // Ensure it flushes before navigation
          await new Promise((resolve) => setTimeout(resolve, 50));

          console.log("Saved LICENSE_USAGE_ID");
        }
      }

      // Navigate to camera after API call
      navigate('/camera-scan', { state: { isEnhanced: false } });
    } catch (error) {
      console.error('Error calling license usage API:', error);
      navigate('/camera-scan', { state: { isEnhanced: false } });
    }
  };

  return (
    <PageContainer>
      <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />

      <SettingsBars
        open={isSettingsOpen}
        onClose={handleCloseSettings}
        cameras={cameras}
        isLicenseValid={isLicenseValid}
      />

      <ContentWrapper>
        <GlassCard>
          <Header>
            <Title>Start Measurement</Title>
            <Subtitle>Select your preferred scan mode to begin assessment</Subtitle>
          </Header>

          <SelectionGrid>
            <SelectionCard variant="enhanced" onClick={handleEnhancedMeasurement}>
              <Badge>Recommended</Badge>
              <IconWrapper style={{ color: '#3b82f6', background: 'rgba(59, 130, 246, 0.05)' }}>
                <Zap />
              </IconWrapper>
              <div>
                <CardTitle>Enhanced Scan</CardTitle>
                <CardDescription style={{ marginTop: '0.5rem' }}>
                  Get comprehensive wellness insights including ASCVD risk score and heart age analysis alongside standard vital signs.
                </CardDescription>
              </div>
              <CardFooter className="arrow-icon">
                <span>Configure & Start</span>
                <ChevronRight size={18} />
              </CardFooter>
            </SelectionCard>

            <SelectionCard onClick={handleBasicMeasurement}>
              <IconWrapper style={{ color: '#64748b' }}>
                <Activity />
              </IconWrapper>
              <div>
                <CardTitle>Basic Scan</CardTitle>
                <CardDescription style={{ marginTop: '0.5rem' }}>
                  Quick vital signs assessment with standard wellness metrics. Best for routine check-ups.
                </CardDescription>
              </div>
              <CardFooter className="arrow-icon">
                <span>Start Direct Scan</span>
                <ChevronRight size={18} />
              </CardFooter>
            </SelectionCard>
          </SelectionGrid>
        </GlassCard>
      </ContentWrapper>
    </PageContainer>
  );
};

export default Scan;